/**
 * CompanyUsers.jsx - Brukeradministrasjon for bedriften
 * 
 * v2.0 - Lisensbegrensning
 * 
 * VIKTIG: All data lagres LOKALT på brukerens maskin.
 * 
 * Funksjonalitet:
 * - Modultilgang per bruker (kun lisensierte moduler)
 * - Upsell-knapp for å se alle moduler og kjøpe flere
 * - Passordadministrasjon (hashed, lagres i localStorage)
 * 
 * KUN TILGJENGELIG FOR company_admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import localAccessService from '../services/localAccessService';
import { 
  Users, 
  Plus, 
  Search,
  ChevronRight,
  Key,
  Layers,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Save,
  Eye,
  EyeOff,
  X,
  HardDrive,
  ShoppingCart,
  Lock,
  Crown,
  Sparkles,
  Baby,
  GraduationCap,
  Heart,
  Briefcase,
  Home,
  ShoppingBag,
  Shield,
  Trash2,
  AlertTriangle,
  UserMinus
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  company_admin: 'Administrator',
  company_user: 'Bruker',
  admin: 'Administrator',
  member: 'Bruker'
};

const STATUS_CONFIG = {
  active: { label: 'Aktiv', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  invited: { label: 'Invitert', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  deactivated: { label: 'Deaktivert', color: 'bg-red-500/20 text-red-400', icon: XCircle }
};

// Ikon-mapping for moduler
const MODULE_ICONS = {
  'barnehage': Baby,
  'skole': GraduationCap,
  'barnevern-master': Heart,
  'barnevern': Shield,
  'arbeidsrett-bedrift': Briefcase,
  'husleierett': Home,
  'forbrukerrett': ShoppingBag,
  'gdpr': Lock,
  'hms': Shield,
  'default': Layers
};

export default function CompanyUsers() {
  const { user, token } = useAuth();
  const { 
    licensedModules, 
    licensedModuleKeys,
    availableForPurchase,
    isModuleLicensed,
    addModuleLicense,
    getUserModuleAccess,
    isAdmin 
  } = useCompany();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showModuleCatalog, setShowModuleCatalog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Module access state (lokal)
  const [userModuleAccess, setUserModuleAccess] = useState({});
  
  // Password state
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Hent bedriftens brukere
  const fetchUsers = useCallback(async () => {
    try {
      const companyRes = await axios.get(`${API}/company/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const companyId = companyRes.data.id;
      
      const usersRes = await axios.get(`${API}/admin/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedUsers = usersRes.data.data?.users || [];
      setUsers(fetchedUsers);
      
      // Lagre brukere lokalt
      fetchedUsers.forEach(u => {
        localAccessService.saveLocalUser(u);
      });
      
    } catch (error) {
      console.error('Failed to fetch users:', error);
      
      // Fallback: Last fra lokal cache
      const localUsers = localAccessService.getAllLocalUsers();
      if (Object.keys(localUsers).length > 0) {
        setUsers(Object.values(localUsers));
      } else {
        setUsers([
          { 
            id: user?.id || 'admin_1', 
            name: user?.name || 'Administrator', 
            email: user?.email, 
            role: 'company_admin', 
            status: 'active',
            last_active: new Date().toISOString()
          }
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Hent modultilgang fra localStorage når bruker velges
  useEffect(() => {
    if (selectedUser) {
      const access = getUserModuleAccess(selectedUser.id);
      setUserModuleAccess(access);
      setNewPassword('');
      setShowPassword(false);
    }
  }, [selectedUser, getUserModuleAccess]);

  // Oppdater modultilgang (lagres LOKALT) - kun for lisensierte moduler
  const handleModuleAccessChange = (moduleKey, allowed) => {
    if (!selectedUser) return;
    
    // Sjekk at modulen er lisensiert
    if (!isModuleLicensed(moduleKey)) {
      toast.error('Denne modulen er ikke i bedriftens lisenspott');
      return;
    }
    
    setUserModuleAccess(prev => ({
      ...prev,
      [moduleKey]: allowed
    }));
    
    const success = localAccessService.setModuleAccess(selectedUser.id, moduleKey, allowed);
    
    if (success) {
      const moduleName = licensedModules.find(m => m.key === moduleKey)?.name || moduleKey;
      toast.success(
        <div className="flex items-center gap-2">
          <HardDrive className="w-4 h-4" />
          <span>{moduleName} {allowed ? 'aktivert' : 'deaktivert'}</span>
        </div>,
        { description: 'Lagret lokalt' }
      );
    } else {
      setUserModuleAccess(prev => ({
        ...prev,
        [moduleKey]: !allowed
      }));
      toast.error('Kunne ikke lagre tilgang');
    }
  };

  // Sett nytt passord (lagres LOKALT, hashed)
  const handleSetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 8) {
      toast.error('Passord må være minst 8 tegn');
      return;
    }
    
    setSavingPassword(true);
    try {
      const success = await localAccessService.setUserPassword(selectedUser.id, newPassword);
      
      if (success) {
        toast.success(
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            <span>Passord oppdatert</span>
          </div>,
          { description: 'Lagret lokalt (kryptert)' }
        );
        setNewPassword('');
        setShowPassword(false);
        
        setUsers(prev => prev.map(u => 
          u.id === selectedUser.id ? { ...u, status: 'active' } : u
        ));
        
        localAccessService.saveLocalUser({
          ...selectedUser,
          status: 'active',
          passwordSetAt: new Date().toISOString()
        });
      } else {
        toast.error('Kunne ikke lagre passord');
      }
    } catch (error) {
      toast.error('Kunne ikke lagre passord');
    } finally {
      setSavingPassword(false);
    }
  };

  // Opprett ny bruker
  const handleCreateUser = async (data) => {
    try {
      const companyRes = await axios.get(`${API}/company/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const res = await axios.post(
        `${API}/admin/companies/${companyRes.data.id}/users`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newUser = res.data.data;
      
      localAccessService.saveLocalUser(newUser);
      localAccessService.setAllModuleAccess(newUser.id, {});
      
      toast.success('Bruker opprettet');
      setShowInviteModal(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Kunne ikke opprette bruker';
      toast.error(message);
    }
  };

  // Simuler kjøp av modul
  const handlePurchaseModule = (moduleKey) => {
    const success = addModuleLicense(moduleKey);
    if (success) {
      const module = availableForPurchase.find(m => m.key === moduleKey);
      toast.success(`${module?.name || moduleKey} lagt til i lisenspotten!`);
      setShowModuleCatalog(false);
    } else {
      toast.error('Kunne ikke legge til modul');
    }
  };

  // Slett bruker
  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    
    setDeleteLoading(true);
    try {
      const companyRes = await axios.get(`${API}/company/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await axios.delete(
        `${API}/admin/companies/${companyRes.data.id}/users/${deleteConfirm.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Fjern fra lokal lagring
      localAccessService.removeLocalUser(deleteConfirm.id);
      
      toast.success('Bruker slettet');
      setDeleteConfirm(null);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Kunne ikke slette bruker';
      toast.error(message);
    }
    setDeleteLoading(false);
  };

  // Fjern all modultilgang for bruker
  const handleRemoveAllAccess = () => {
    if (!selectedUser) return;
    
    // Sett alle moduler til false
    const clearedAccess = {};
    licensedModuleKeys.forEach(key => {
      clearedAccess[key] = false;
    });
    
    setUserModuleAccess(clearedAccess);
    localAccessService.setAllModuleAccess(selectedUser.id, clearedAccess);
    
    toast.success(
      <div className="flex items-center gap-2">
        <UserMinus className="w-4 h-4" />
        <span>All modultilgang fjernet</span>
      </div>,
      { description: `${selectedUser.name} har nå ingen modultilgang` }
    );
  };

  // Gi bruker tilgang til alle moduler
  const handleGrantAllAccess = () => {
    if (!selectedUser) return;
    
    const fullAccess = {};
    licensedModuleKeys.forEach(key => {
      fullAccess[key] = true;
    });
    
    setUserModuleAccess(fullAccess);
    localAccessService.setAllModuleAccess(selectedUser.id, fullAccess);
    
    toast.success(
      <div className="flex items-center gap-2">
        <CheckCircle className="w-4 h-4" />
        <span>Full modultilgang gitt</span>
      </div>,
      { description: `${selectedUser.name} har nå tilgang til alle moduler` }
    );
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sjekk om modul er aktivert for bruker
  const isModuleAllowed = (moduleKey) => {
    return userModuleAccess[moduleKey] === true;
  };

  // Sjekk om valgt bruker er admin
  const isSelectedUserAdmin = selectedUser?.role === 'company_admin' || selectedUser?.role === 'admin';

  // Tell antall moduler bruker har tilgang til
  const getUserModuleCount = (userId) => {
    const access = getUserModuleAccess(userId);
    return Object.values(access).filter(v => v === true).length;
  };

  // Hent ikon for modul
  const getModuleIcon = (moduleKey) => {
    return MODULE_ICONS[moduleKey] || MODULE_ICONS.default;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#B87333]" />
          <span className="text-[#8899A8]">Laster brukere...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="company-users">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Brukere</h1>
          <p className="text-[#8899A8] mt-1">
            {users.length} bruker{users.length !== 1 ? 'e' : ''} i din bedrift
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Upsell-knapp */}
          <button
            onClick={() => setShowModuleCatalog(true)}
            data-testid="view-all-modules-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1B2631] border border-[#B87333]/50 hover:border-[#B87333] text-[#B87333] rounded-lg transition-colors font-medium"
          >
            <ShoppingCart className="w-4 h-4" />
            Se alle moduler
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            data-testid="invite-user-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#B87333] hover:bg-[#A06328] text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Ny bruker
          </button>
        </div>
      </div>

      {/* Lisenspott-oversikt */}
      <div className="bg-gradient-to-r from-[#B87333]/10 to-[#B87333]/5 border border-[#B87333]/30 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#B87333]" />
            <span className="text-sm font-semibold text-white">Din lisenspott</span>
          </div>
          <span className="text-xs text-[#8899A8]">
            {licensedModuleKeys.length} modul{licensedModuleKeys.length !== 1 ? 'er' : ''} tilgjengelig
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {licensedModules.map(module => {
            const Icon = getModuleIcon(module.key);
            return (
              <div 
                key={module.key}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg"
              >
                <Icon className="w-4 h-4 text-[#B87333]" />
                <span className="text-sm text-white">{module.name}</span>
                {module.premium && (
                  <Sparkles className="w-3 h-3 text-[#B87333]" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Info om lokal lagring */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
        <HardDrive className="w-5 h-5 text-[#22c55e] flex-shrink-0" />
        <p className="text-sm text-[#22c55e]">
          All tilgang og passord lagres lokalt på din maskin. Ingenting sendes til serveren.
        </p>
      </div>

      {/* Søk */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8899A8]" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Søk etter bruker..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Brukerliste */}
        <div className="lg:col-span-2">
          {filteredUsers.length === 0 ? (
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-12 text-center">
              <Users className="w-12 h-12 text-[#8899A8] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {searchQuery ? 'Ingen resultater' : 'Ingen brukere ennå'}
              </h3>
            </div>
          ) : (
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl overflow-hidden">
              <div className="divide-y divide-[#2C3E50]">
                {filteredUsers.map((u) => {
                  const statusConfig = STATUS_CONFIG[u.status] || STATUS_CONFIG.invited;
                  const StatusIcon = statusConfig.icon;
                  const isSelected = selectedUser?.id === u.id;
                  const isCurrentUser = u.email === user?.email;
                  const hasPassword = localAccessService.hasPasswordSet(u.id);
                  const moduleCount = getUserModuleCount(u.id);
                  const isUserAdmin = u.role === 'company_admin' || u.role === 'admin';
                  
                  return (
                    <button
                      key={u.id}
                      onClick={() => !isCurrentUser && setSelectedUser(u)}
                      disabled={isCurrentUser}
                      className={`
                        w-full p-4 text-left transition-colors
                        ${isSelected ? 'bg-[#B87333]/10' : 'hover:bg-[#2C3E50]/30'}
                        ${isCurrentUser ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#B87333]/20 flex items-center justify-center">
                            <span className="text-sm font-medium text-[#B87333]">
                              {u.name?.charAt(0)?.toUpperCase() || 'B'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white flex items-center gap-2">
                              {u.name}
                              {isCurrentUser && (
                                <span className="text-xs text-[#8899A8]">(deg)</span>
                              )}
                              {isUserAdmin && (
                                <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#B87333] text-white rounded font-bold">
                                  <Crown className="w-3 h-3" />
                                  ADMIN
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-[#8899A8]">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Modultilgang-teller */}
                          {!isUserAdmin && (
                            <span className="text-xs px-2 py-1 bg-[#2C3E50] text-[#8899A8] rounded">
                              {moduleCount} modul{moduleCount !== 1 ? 'er' : ''}
                            </span>
                          )}
                          {hasPassword && (
                            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                              Passord satt
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                          {!isCurrentUser && (
                            <ChevronRight className={`w-4 h-4 text-[#8899A8] transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Detaljer-panel */}
        <div className="lg:col-span-1">
          {selectedUser ? (
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-5 space-y-6 sticky top-6">
              {/* Brukerinfo */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#B87333]/20 flex items-center justify-center">
                    <span className="text-lg font-medium text-[#B87333]">
                      {selectedUser.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{selectedUser.name}</p>
                    <p className="text-sm text-[#8899A8]">{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-1.5 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* ─────────────────────────────────────────────────────────────── */}
              {/* MODULTILGANG (kun lisensierte moduler) */}
              {/* ─────────────────────────────────────────────────────────────── */}
              {!isSelectedUserAdmin && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-[#B87333]" />
                      <h3 className="text-sm font-medium text-white">Modultilgang</h3>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#2C3E50] text-[#8899A8] rounded">
                      Fra lisenspott
                    </span>
                  </div>
                  
                  {/* Hurtigknapper */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={handleGrantAllAccess}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-[#22c55e]/10 hover:bg-[#22c55e]/20 text-[#22c55e] rounded-lg transition-colors"
                      data-testid="grant-all-access-btn"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Gi alle
                    </button>
                    <button
                      onClick={handleRemoveAllAccess}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      data-testid="remove-all-access-btn"
                    >
                      <UserMinus className="w-3 h-3" />
                      Fjern alle
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {licensedModules.map((module) => {
                      const allowed = isModuleAllowed(module.key);
                      const Icon = getModuleIcon(module.key);
                      
                      return (
                        <label
                          key={module.key}
                          className="flex items-center gap-3 p-2.5 bg-[#0F1419] rounded-lg cursor-pointer hover:bg-[#0F1419]/80"
                          data-testid={`module-checkbox-${module.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={allowed}
                            onChange={(e) => handleModuleAccessChange(module.key, e.target.checked)}
                            className="w-4 h-4 rounded border-[#2C3E50] bg-[#0F1419] text-[#B87333] focus:ring-[#B87333] focus:ring-offset-0"
                          />
                          <Icon className="w-4 h-4 text-[#8899A8]" />
                          <span className="text-sm text-white flex-1">{module.name}</span>
                          {module.premium && (
                            <Sparkles className="w-3 h-3 text-[#B87333]" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                  
                  {/* Kjøp flere moduler */}
                  {availableForPurchase.length > 0 && (
                    <button
                      onClick={() => setShowModuleCatalog(true)}
                      className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-[#B87333]/50 text-[#B87333] rounded-lg hover:bg-[#B87333]/10 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Kjøp flere tilganger
                    </button>
                  )}
                </div>
              )}

              {isSelectedUserAdmin && (
                <div className="p-3 bg-[#B87333]/10 border border-[#B87333]/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4 text-[#B87333]" />
                    <span className="text-sm font-medium text-[#B87333]">Organisasjons-Admin</span>
                  </div>
                  <p className="text-xs text-[#8899A8]">
                    Administratorer har automatisk tilgang til alle lisensierte moduler.
                  </p>
                </div>
              )}

              {/* ─────────────────────────────────────────────────────────────── */}
              {/* PASSORDADMINISTRASJON */}
              {/* ─────────────────────────────────────────────────────────────── */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Key className="w-4 h-4 text-[#B87333]" />
                  <h3 className="text-sm font-medium text-white">Passordadministrasjon</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Nytt passord (min. 8 tegn)"
                      data-testid="password-input"
                      className="w-full px-3 py-2.5 pr-10 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8899A8] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  <button
                    onClick={handleSetPassword}
                    disabled={!newPassword || newPassword.length < 8 || savingPassword}
                    data-testid="set-password-btn"
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#B87333] hover:bg-[#A06328] disabled:bg-[#2C3E50] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {savingPassword ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>Sett passord</span>
                  </button>
                </div>
              </div>

              {/* ─────────────────────────────────────────────────────────────── */}
              {/* SLETT BRUKER */}
              {/* ─────────────────────────────────────────────────────────────── */}
              {!isSelectedUserAdmin && (
                <div className="pt-4 border-t border-[#2C3E50]">
                  <button
                    onClick={() => setDeleteConfirm(selectedUser)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    data-testid="delete-user-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Slett bruker</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-8 text-center">
              <Users className="w-10 h-10 text-[#8899A8] mx-auto mb-3" />
              <p className="text-sm text-[#8899A8]">
                Velg en bruker for å administrere tilgang og passord
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showInviteModal && (
        <CreateUserModal
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleCreateUser}
        />
      )}
      
      {/* Module Catalog Modal (Upsell) */}
      {showModuleCatalog && (
        <ModuleCatalogModal
          licensedModules={licensedModules}
          availableModules={availableForPurchase}
          onPurchase={handlePurchaseModule}
          onClose={() => setShowModuleCatalog(false)}
        />
      )}
      
      {/* Delete User Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1B2631] rounded-xl w-full max-w-md border border-[#2C3E50]" data-testid="delete-user-modal">
            <div className="flex items-center justify-between p-4 border-b border-[#2C3E50]">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Slett bruker
              </h3>
              <button 
                onClick={() => setDeleteConfirm(null)}
                className="p-1 hover:bg-[#2C3E50] rounded"
              >
                <X className="w-5 h-5 text-[#8899A8]" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-[#8899A8] mb-4">
                Er du sikker på at du vil slette brukeren <span className="text-white font-medium">{deleteConfirm.name}</span> ({deleteConfirm.email})?
              </p>
              <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg text-sm text-red-400">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Denne handlingen:</p>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    <li>• Fjerner brukeren fra bedriften</li>
                    <li>• Sletter all modultilgang</li>
                    <li>• Kan ikke angres</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-4 border-t border-[#2C3E50]">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-[#8899A8] hover:text-white"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50"
                data-testid="confirm-delete-user-btn"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Slett permanent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE USER MODAL
// ═══════════════════════════════════════════════════════════════

function CreateUserModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'member'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Opprett ny bruker</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-[#8899A8] mb-1">Navn</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Fullt navn"
              required
              className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#8899A8] mb-1">E-post</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="bruker@bedrift.no"
              required
              className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#8899A8] mb-1">Rolle</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white focus:outline-none focus:border-[#B87333]"
            >
              <option value="member">Bruker</option>
              <option value="admin">Administrator</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-[#2C3E50] text-white rounded-lg hover:bg-[#2C3E50] transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name || !formData.email}
              className="flex-1 px-4 py-2.5 bg-[#B87333] hover:bg-[#A06328] disabled:bg-[#2C3E50] text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Opprett
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODULE CATALOG MODAL (UPSELL)
// ═══════════════════════════════════════════════════════════════

function ModuleCatalogModal({ licensedModules, availableModules, onPurchase, onClose }) {
  // Ikon-mapping
  const getIcon = (key) => {
    const icons = {
      'barnehage': Baby,
      'skole': GraduationCap,
      'barnevern-master': Heart,
      'barnevern': Shield,
      'arbeidsrett-bedrift': Briefcase,
      'husleierett': Home,
      'forbrukerrett': ShoppingBag,
      'gdpr': Lock,
      'hms': Shield,
    };
    return icons[key] || Layers;
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-[#2C3E50]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Modulkatalog</h2>
              <p className="text-sm text-[#8899A8] mt-1">
                Se alle tilgjengelige moduler og utvid abonnementet ditt
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Dine moduler */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#22c55e]" />
              Dine lisensierte moduler ({licensedModules.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {licensedModules.map(module => {
                const Icon = getIcon(module.key);
                return (
                  <div 
                    key={module.key}
                    className="flex items-center gap-3 p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl"
                  >
                    <div className="p-2 rounded-lg bg-[#22c55e]/20">
                      <Icon className="w-5 h-5 text-[#22c55e]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{module.name}</p>
                      <p className="text-xs text-[#8899A8]">{module.toolCount} verktøy</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Tilgjengelige moduler */}
          {availableModules.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#B87333]" />
                Tilgjengelige moduler ({availableModules.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableModules.map(module => {
                  const Icon = getIcon(module.key);
                  const isComing = module.status === 'coming';
                  
                  return (
                    <div 
                      key={module.key}
                      className={`
                        flex items-start gap-3 p-4 rounded-xl border transition-all
                        ${isComing 
                          ? 'bg-[#0F1419]/50 border-[#2C3E50] opacity-60' 
                          : 'bg-[#0F1419] border-[#2C3E50] hover:border-[#B87333]/50'
                        }
                      `}
                    >
                      <div className={`p-2 rounded-lg ${isComing ? 'bg-[#2C3E50]' : 'bg-[#B87333]/20'}`}>
                        <Icon className={`w-5 h-5 ${isComing ? 'text-[#8899A8]' : 'text-[#B87333]'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium text-white">{module.name}</p>
                          {module.premium && (
                            <Sparkles className="w-3 h-3 text-[#B87333]" />
                          )}
                        </div>
                        <p className="text-xs text-[#8899A8] mb-2">{module.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#8899A8]">{module.toolCount} verktøy</span>
                          {isComing ? (
                            <span className="text-xs px-2 py-1 bg-[#2C3E50] text-[#8899A8] rounded">
                              Kommer snart
                            </span>
                          ) : (
                            <button
                              onClick={() => onPurchase(module.key)}
                              className="text-xs px-3 py-1.5 bg-[#B87333] hover:bg-[#A06328] text-white rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Legg til ({module.price} kr/år)
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[#2C3E50] bg-[#0F1419]">
          <p className="text-xs text-[#8899A8] text-center">
            Alle priser er eks. mva. Kontakt salg for volumrabatter og tilpassede løsninger.
          </p>
        </div>
      </div>
    </div>
  );
}
